import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ReactComponent as EnterIconSVG } from '../../assets/images/enter-icon.svg';
import { ReactComponent as StatusErrorSVG } from '../../assets/images/status-error.svg';
import { nextTick } from "process";
import LoadingSpinner from "../LoadingSpinner";
import toast from "react-hot-toast";
import { Back4app2Error } from "@back4app2/sdk";

// Stop icon component (square shape commonly used for stop)
const StopIcon = ({ width = "20", height = "20" }: { width?: string; height?: string }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

const AgentInput = forwardRef<
  { setContent: (content: string) => void },
  { 
    onSend: (content: string) => Promise<boolean>; 
    onStop?: () => Promise<boolean>;
    disabled?: boolean;
    isResponding?: boolean;
  }
>(({ onSend, onStop, disabled, isResponding }, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [content, setContent] = useState<string>();
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isStopping, setIsStopping] = useState<boolean>(false);
  const stoppingToastId = useRef<string | null>(null);
  const isBlocked = !!disabled || isSending || isStopping;

  // Reset isStopping when response finishes (isResponding becomes false)
  useEffect(() => {
    if (!isResponding && isStopping) {
      setIsStopping(false);
      // Dismiss the stopping toast when response finishes
      if (stoppingToastId.current) {
        toast.dismiss(stoppingToastId.current);
        stoppingToastId.current = null;
      }
    }
  }, [isResponding, isStopping]);

  const handleStop = async () => {
    if (isStopping || !onStop) return;
    setIsStopping(true);
    
    // Show stopping toast
    stoppingToastId.current = toast.loading('Stopping generation...', {
      className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
    });
    
    try {
      const success = await onStop();
      
      if (!success) {
        // This shouldn't happen since API now throws on all failures
        // Keep as fallback just in case
        setIsStopping(false);
        if (stoppingToastId.current) {
          toast.dismiss(stoppingToastId.current);
          stoppingToastId.current = null;
        }
        return;
      }
      // Stop was sent successfully - wait for response to finish
    } catch (e) {
      // Only reset on error, otherwise wait for response to finish
      setIsStopping(false);
      if (stoppingToastId.current) {
        toast.dismiss(stoppingToastId.current);
        stoppingToastId.current = null;
      }
      
      // Show the error message from the API
      const errorMessage = e instanceof Back4app2Error 
        ? e.message 
        : 'Failed to stop generation';
      
      toast.error(errorMessage, {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 3000,
      });
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setContent(value);
  };

  useImperativeHandle(ref, () => ({
    setContent: (content: string) => {
      if (!isSending) {
        setContent(content);
        textareaRef.current?.focus();
      }
    }
  }));

  useEffect(
    () => {
      textareaRef.current?.focus();
    },
    []
  );

  useEffect(
    () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        const scrollHeight = textareaRef.current.scrollHeight;
        textareaRef.current.style.height = scrollHeight + 'px';
      }
    },
    [content]
  );

  const send = async () => {
    if (isBlocked) return;
    if (content) {
      setIsSending(true);
      if (await onSend(content)) {
        setContent('');
      }
      setIsSending(false);
      nextTick(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      });
    } else if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <div className="absolute w-full bottom-0 left-0 rounded-lg pb-0">
      {/* Flat, clean input (Lovable-like) using Back4App colors */}
      <div className="mx-0 p-4">
        <div className={`w-full flex items-center gap-3 glass-input glass-input-opaque px-4 py-3 focus-within:ring-2 focus-within:ring-[#208AEC]/60 ${isStopping ? 'pointer-events-none opacity-70' : ''}`}>
          <textarea
            disabled={isBlocked}
            className={`m-0 flex-1 resize-none bg-transparent outline-none text-white placeholder:text-light-grey/70 leading-6 ${isStopping ? 'cursor-not-allowed' : ''}`}
            style={{ maxHeight: '120px' }}
            ref={textareaRef}
            placeholder={'Ask back4app...'}
            value={content}
            rows={1}
            onChange={handleChange}
            onKeyDown={async (event) => {
              if (event.key.toLowerCase() === 'enter') {
                event.preventDefault();
                if (isBlocked) return;
                if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
                  if (textareaRef.current) {
                    const startPos = textareaRef.current.selectionStart;
                    const endPos = textareaRef.current.selectionEnd;
                    const value = textareaRef.current.value;
                    const newValue = value.substring(0, startPos) + '\n' + value.substring(endPos);
                    setContent(newValue);
                    nextTick(() => {
                      if (textareaRef.current) {
                        textareaRef.current.setSelectionRange(startPos + 1, startPos + 1);
                        if (textareaRef.current.value.length === startPos + 1) {
                          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
                        }
                      }
                    });
                  }
                } else {
                  send();
                }
              }
            }}
          />
          {isResponding && onStop ? (
            // Show stop button when agent is responding
            <button
              onClick={handleStop}
              disabled={isStopping}
              className={`flex-none inline-flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                isStopping 
                  ? 'bg-system-yellow/20 text-system-yellow pointer-events-none opacity-50' 
                  : 'bg-error-red/20 text-error-red hover:bg-error-red/30'
              }`}
              aria-label={isStopping ? "Stopping generation" : "Stop generation"}
              title={isStopping ? "Stopping..." : "Stop generation"}
            >
              {isStopping ? (
                <LoadingSpinner width='1.25rem' height='1.25rem' />
              ) : (
                <StopIcon width="20" height="20" />
              )}
            </button>
          ) : isBlocked ? (
            <LoadingSpinner width='2rem' height='2rem' />
          ) : (
            <button
              onClick={send}
              className="flex-none inline-flex items-center justify-center w-10 h-10 rounded-md bg-white/10 text-light-blue hover:bg-white/15 transition-colors"
              aria-label="Send message"
            >
              <EnterIconSVG width="20" height="20" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
});

export default AgentInput;